/**
 * Utility functions for generating consistent data-testid attributes
 * across the Wildfire TimeTracking application
 */

// Test ID prefixes based on existing patterns in the codebase
export const TEST_ID_PREFIXES = {
  // Form fields and inputs
  FIELD: 'field',
  // Buttons and actions
  BUTTON: 'btn', 
  // Table elements
  TABLE: 'table',
  // Modal elements
  MODAL: 'modal',
  // Navigation elements
  NAV: 'nav',
  // Filter elements (existing pattern)
  FILTER: 'fltr',
  // Page elements
  PAGE: 'page',
  // Autocomplete fields in forms (existing pattern)
  FORM_AUTOCOMPLETE: 'tnc',
  // Dropdown/select fields (existing pattern)  
  FORM_SELECT: 'tic',
  // Modal dialog buttons (existing pattern)
  MODAL_BUTTON: 'ud',
  // Search elements
  SEARCH: 'search',
  // Accordion sections
  ACCORDION: 'acc'
} as const;

/**
 * Generate a test ID for form fields
 * @param fieldName The name of the form field
 * @returns Formatted test ID string
 */
export const createFieldTestId = (fieldName: string): string => {
  return `${TEST_ID_PREFIXES.FIELD}-${fieldName}`;
};

/**
 * Generate a test ID for buttons
 * @param action The action the button performs
 * @returns Formatted test ID string
 */
export const createButtonTestId = (action: string): string => {
  return `${TEST_ID_PREFIXES.BUTTON}-${action}`;
};

/**
 * Generate a test ID for table elements
 * @param tableName The name of the table
 * @param element The specific element (row, cell, header, etc.)
 * @param index Optional index for dynamic elements
 * @returns Formatted test ID string
 */
export const createTableTestId = (tableName: string, element: string, index?: number | string): string => {
  const base = `${TEST_ID_PREFIXES.TABLE}-${tableName}-${element}`;
  return index !== undefined ? `${base}-${index}` : base;
};

/**
 * Generate a test ID for modal elements
 * @param modalName The name of the modal
 * @param element The specific element within the modal
 * @returns Formatted test ID string
 */
export const createModalTestId = (modalName: string, element: string): string => {
  return `${TEST_ID_PREFIXES.MODAL}-${modalName}-${element}`;
};

/**
 * Generate a test ID for navigation elements
 * @param element The navigation element
 * @returns Formatted test ID string
 */
export const createNavTestId = (element: string): string => {
  return `${TEST_ID_PREFIXES.NAV}-${element}`;
};

/**
 * Generate a test ID for page-level elements
 * @param element The page element
 * @returns Formatted test ID string
 */
export const createPageTestId = (element: string): string => {
  return `${TEST_ID_PREFIXES.PAGE}-${element}`;
};

/**
 * Generate a test ID for search elements
 * @param element The search element
 * @returns Formatted test ID string
 */
export const createSearchTestId = (element: string): string => {
  return `${TEST_ID_PREFIXES.SEARCH}-${element}`;
};

/**
 * Generate a test ID for accordion sections
 * @param sectionName The name of the accordion section
 * @param element Optional specific element within the section
 * @returns Formatted test ID string
 */
export const createAccordionTestId = (sectionName: string, element?: string): string => {
  const base = `${TEST_ID_PREFIXES.ACCORDION}-${sectionName}`;
  return element ? `${base}-${element}` : base;
};

/**
 * Generate a test ID for filter elements (extends existing fltr_ pattern)
 * @param filterName The name of the filter
 * @returns Formatted test ID string
 */
export const createFilterTestId = (filterName: string): string => {
  return `${TEST_ID_PREFIXES.FILTER}_${filterName}`;
};

/**
 * Generate a test ID for form autocomplete elements (extends existing tnc_ pattern)
 * @param fieldName The name of the field
 * @param index Optional index for dynamic elements
 * @returns Formatted test ID string
 */
export const createFormAutocompleteTestId = (fieldName: string, index?: number | string): string => {
  const base = `${TEST_ID_PREFIXES.FORM_AUTOCOMPLETE}_${fieldName}`;
  return index !== undefined ? `${base}-${index}` : base;
};

/**
 * Generate a test ID for form select/dropdown elements (extends existing tic_ pattern)
 * @param fieldName The name of the field
 * @param index Optional index for dynamic elements
 * @returns Formatted test ID string
 */
export const createFormSelectTestId = (fieldName: string, index?: number | string): string => {
  const base = `${TEST_ID_PREFIXES.FORM_SELECT}_${fieldName}`;
  return index !== undefined ? `${base}-${index}` : base;
};

/**
 * Generate a test ID for modal dialog buttons (extends existing ud_ pattern)
 * @param modalName The name of the modal
 * @param action The button action (yes, no, cancel, etc.)
 * @returns Formatted test ID string
 */
export const createModalButtonTestId = (modalName: string, action: string): string => {
  return `${TEST_ID_PREFIXES.MODAL_BUTTON}-${modalName}-${action}`;
};

/**
 * Sanitize a string for use in test IDs by replacing spaces and special characters
 * @param input The input string to sanitize
 * @returns Sanitized string suitable for test IDs
 */
export const sanitizeForTestId = (input: string): string => {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

/**
 * Generate a compound test ID for complex form elements
 * @param parts Array of parts to join with hyphens
 * @returns Formatted compound test ID string
 */
export const createCompoundTestId = (parts: (string | number)[]): string => {
  return parts
    .map(part => typeof part === 'string' ? sanitizeForTestId(part) : part.toString())
    .join('-');
};